// Astra's open chat, the landing page's hero (#2725; founder pick B of the
// "Ask Anything" mocks, 2026-10-01). Anyone asks anything the knowledge base
// covers, with no self-description first: the question box leads, topic tabs
// with three live-checked seeds each sit under it, and "Make it about me"
// (the picture flow, optional) waits in the panel header.
//
// What the server decides and the page only shows: the answer's words (a safe
// markdown subset), the tables it drew from the read, the dated sources, the
// topic, whether the answer was useful (it stood on a real read), the income
// band ask, the personal-read offer, the day's free questions and how many
// are left. The page never hard-codes the daily number.
//
// Measurement: astra_chat_* events (analytics.js), none carrying a word the
// visitor typed. Tab taps say what someone came for before they type.
//
// The same chat runs as a college page's sheet (#3262, founder pick A of the
// Astra entry mocks, 2026-10-06): `variant="sheet"` with the page's `school`.
// It opens on the school's own seeds (no tabs), asks the seed or typed
// question it was opened with, names the school in its header, and keeps no
// conversation from the landing page on screen (it still keeps its own for
// the tab, so a sign-in from the sheet carries it, #2945). A typed question
// that does not name the school goes as "About UCLA: ..." until the sheet's
// first answer, after which the server's held school carries it.
//
// After an answer about where someone stands or what a school costs, with a
// school read, the server offers the personal read (`personalize`) and the
// page shows one quiet card (Set 2 pick A): one line, one button into "Make it
// about me", preset for that school. It replaces the sign-in nudge on that
// answer. Its words never ask for scores (product invariant 4).
//
// After any other useful answer that named a school (a deadline, a cost, a
// major, an essay prompt, a News story; #5229, founder 2026-10-23 on the NYU
// deadline answer), the nudge box leads with the same free read as the next
// step (content/astraChat.js READ_LEAD, one honest line per topic) and its
// button opens "Make it about me" preset to that school by name; the
// topic's sign-in line follows, quieter, on its own entry as before.
//
// A News story's sheet (#3272, founder pick A of the News Astra mocks,
// 2026-10-06) is the same sheet: with the story's school (a school's own
// story) or with no school at all (a general story), `story` naming it for
// the events (scope 'news'), the story's seeds, and `words` for the header,
// greeting, box and close button. Its sign-in line is the mock's honest one
// (`nudge`), shown once after the first useful answer like the topic nudge.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRightIcon, CheckIcon, SparklesIcon, UserIcon } from '@heroicons/react/24/outline';
import AstraAvatar from '../../concierge/AstraAvatar';
import { StartFree } from '../home/parts';
import AstraComposer from '../astraLite/AstraComposer';
import AstraLite, { AstraBubble } from '../astraLite/AstraLite';
import RichAnswer from './RichAnswer';
import {
    CHAT, CHAT_TABS, LIMIT, QUESTION_SAMPLES, limitItems, nudgeByKey, nudgeFor, readLeadFor,
} from '../../../content/astraChat';
import {
    astraChatAsk, astraChatAskStream, astraStatus, chatToken, keepChatToken, pageTimeForAsk, previewToken,
} from '../../../services/astraLite';
import { track, EVENTS } from '../../../services/analytics';
import { REPLAY_PRIVATE_CLASS } from '../../../services/analyticsPrivacy';
import { SHEET, scopedQuestion } from '../../../content/collegeAsk';
import { clearChatThread, loadChatThread, saveChatThread } from '../../../services/astraChatThread';
import { UNIVERSITY_COUNT } from '../../../constants/catalog';
import { COUNTER_FROM } from '../../../content/astraLite';
import { POPULAR, insightLine, liteChips, rankSeeds } from '../../../content/astraPopular';

const MAX_HISTORY = 6;
export { COUNTER_FROM };
// The sign-in entries (content/landingIds.js), one per nudge topic, written
// out so the landing drift test reads each one from this file.
const CTA_IDS = {
    limitCta: 'astra_chat_limit',
    newsCta: 'astra_chat_nudge_news',
    deadlinesCta: 'astra_chat_nudge_deadlines',
    costCta: 'astra_chat_nudge_cost',
    oddsCta: 'astra_chat_nudge_odds',
    majorsCta: 'astra_chat_nudge_majors',
    essaysCta: 'astra_chat_nudge_essays',
    summerCta: 'astra_chat_nudge_summer',
    recordCta: 'astra_chat_nudge_record',
    resultsCta: 'astra_chat_nudge_results',
    // #5229: the nudge's read button (opens the free read, not sign-in)
    readCta: 'astra_chat_nudge_read',
    // #5237: "Sign in instead" beside Build my picture on Make it about me
    signinInsteadCta: 'astra_chat_signin_instead',
};
export const CHAT_CTA = {
    limit: CTA_IDS.limitCta,
    nudge: (key) => CTA_IDS[`${key}Cta`] || CTA_IDS.recordCta,
    read: CTA_IDS.readCta,
    signinInstead: CTA_IDS.signinInsteadCta,
};

// Clear of the fixed site header (about 64px, with room to breathe).
const HEADER_CLEARANCE = 88;
// How much of the last answer stays in view above the "Make it about me"
// step (#2896, mock A): the end of the conversation shows above the new
// turn, so the student sees it continued. Live at 375px with the step flush
// under the header, only a sliver of the sources card showed.
const ANSWER_PEEK = 96;
// Bring an element's top to just under the fixed header (plus `peek` px of
// what sits above it): smooth, or instant for anyone who asked the system
// for less motion.
function scrollBelowHeader(el, peek = 0) {
    if (!el || typeof window.scrollTo !== 'function') return;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const top = el.getBoundingClientRect().top + window.scrollY - HEADER_CLEARANCE - peek;
    try { window.scrollTo({ top: Math.max(0, top), behavior: reduced ? 'auto' : 'smooth' }); } catch { /* old browsers */ }
}
// Inside the college page's sheet the scroller is the sheet, not the window.
function bringIntoView(el, sheet, peek = 0) {
    if (!el) return;
    if (!sheet) { scrollBelowHeader(el, peek); return; }
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    try { el.scrollIntoView?.({ block: 'start', behavior: reduced ? 'auto' : 'smooth' }); } catch { /* old browsers */ }
}
// The last answer's start (a nudge or offer may follow it).
function lastAnswerIn(threadEl) {
    const items = threadEl?.querySelectorAll('[data-testid="astra-chat-answer"], [data-testid="astra-chat-crisis"]');
    return items && items.length ? items[items.length - 1] : null;
}

// Blocked in session replay (#4475): the visitor's own words never record.
function VisitorBubble({ children }) {
    return (
        <div className={`${REPLAY_PRIVATE_CLASS} text-right`} data-turn="visitor">
            <span className="inline-block max-w-[85%] whitespace-pre-wrap rounded-2xl bg-[#1A4D2E] px-3.5 py-2.5 text-left text-sm leading-relaxed text-white">
                {children}
            </span>
        </div>
    );
}

// The box after the first useful answer. With a school named and a topic
// the free read fits (#5229, `nudge.read`), it leads with the read as the
// next step and its button opens "Make it about me" preset to that school;
// the topic's sign-in line follows, quieter, on its own entry. Otherwise
// (no school, or the record, summer and results topics) the sign-in line
// leads as it always has. Same box, same place either way.
function Nudge({ nudge, onStart, onRead }) {
    const read = Boolean(nudge.read);
    return (
        <div data-testid="astra-chat-nudge" data-read={read ? 'on' : 'off'}
            className="ml-9 flex items-start gap-2.5 rounded-xl border border-[#CFE0D2] bg-white px-3 py-2.5">
            <SparklesIcon className="mt-0.5 h-4 w-4 shrink-0 text-[#8A6B1F]" aria-hidden="true" />
            <div className="min-w-0">
                {read && (
                    <>
                        <p data-testid="astra-chat-nudge-read" className="text-[13px] font-semibold leading-snug text-[#2C2C2C]">{nudge.read}</p>
                        <button type="button" data-cta={CHAT_CTA.read} data-testid="astra-chat-nudge-read-go" onClick={() => onRead(nudge)}
                            className="mt-2 inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-[#1A4D2E] px-4 text-sm font-semibold text-white">
                            <UserIcon className="h-4 w-4" aria-hidden="true" /> {CHAT.nudgeReadButton}
                        </button>
                    </>
                )}
                <p className={read ? 'mt-2.5 text-xs leading-snug text-[#4A4A4A]' : 'text-[13px] leading-snug text-[#2C2C2C]'}>{nudge.line}</p>
                <button type="button" data-cta={CHAT_CTA.nudge(nudge.key)} data-testid="astra-chat-nudge-signin" onClick={() => onStart(nudge)}
                    className={`inline-flex min-h-[44px] items-center gap-1 font-semibold text-[#1A4D2E] underline underline-offset-4 ${read ? 'text-xs' : 'text-sm'}`}>
                    Start free <ArrowRightIcon className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
            </div>
        </div>
    );
}

// Set 2 pick A (#3262): the quiet card under an odds or cost answer.
function PersonalOffer({ offer, topic, onOpen }) {
    const cost = nudgeFor(topic).key === 'cost';
    return (
        <div data-testid="astra-chat-personal-offer" className="ml-9 rounded-xl border border-[#CFE0D2] bg-[#F4F8F3] p-3.5">
            <p className="text-sm font-semibold text-[#2C2C2C]">{cost ? CHAT.offerTitleCost(offer.name) : CHAT.offerTitle}</p>
            <p className="mt-1 text-[13px] leading-snug text-[#4A4A4A]">{CHAT.offerLine(offer.name)}</p>
            <button type="button" onClick={onOpen} data-testid="astra-chat-personal-open"
                className="mt-2.5 inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-[#1A4D2E] px-4 text-sm font-semibold text-white">
                <UserIcon className="h-4 w-4" aria-hidden="true" /> {CHAT.offerButton}
            </button>
        </div>
    );
}

function LimitCard({ total, topics, buildNames, onStart }) {
    return (
        <div data-testid="astra-chat-limit" className="rounded-2xl bg-[#1A4D2E] p-5 text-left sm:p-6">
            <div className="flex items-center gap-2">
                <AstraAvatar size={28} />
                <p className="font-semibold text-white">{LIMIT.title(total)}</p>
            </div>
            <p className="mt-2 text-sm text-[#C7E3CB]">{LIMIT.lead}</p>
            <ul className="mt-3 space-y-2">
                {limitItems(topics).map((line) => (
                    <li key={line} className="flex gap-2 text-sm text-white">
                        <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-[#9FD3A8]" aria-hidden="true" />{line}
                    </li>
                ))}
            </ul>
            {buildNames.length > 0 && (
                <>
                    <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-[#C7E3CB]">{LIMIT.buildLabel}</p>
                    <ul className="mt-2 flex flex-wrap gap-1.5" data-testid="astra-chat-limit-build">
                        {buildNames.map((b) => (
                            <li key={b} className="rounded-full border border-white/30 px-3 py-1 text-xs font-semibold text-white">{b}</li>
                        ))}
                    </ul>
                </>
            )}
            <StartFree onStart={onStart} cta={CHAT_CTA.limit} tone="white" size="md" className="mt-5 w-full sm:w-auto" />
            <p className="mt-2 text-xs text-[#C7E3CB]">{LIMIT.small(total)}</p>
        </div>
    );
}

function Honeypot({ value, onChange }) {
    return (
        <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true"
            data-lpignore="true" data-1p-ignore="true" data-bwignore="true" data-form-type="other"
            value={value} onChange={(e) => onChange(e.target.value)}
            style={{ position: 'absolute', left: '-10000px', width: 1, height: 1, opacity: 0 }} />
    );
}

// `onStart(cta)`: the page's sign-in, with the CTA id as the entry.
// `onUnavailable()`: the route is closed or its budget spent before any
// answer; the page shows the shipped free read in this place instead.
// `buildNames`: the Build actions the page shows.
// The sheet (#3262): `variant="sheet"`, `school` (the college page's entry:
// {id, slug, name, shortName, testBlind, aliases}), `seeds` ([{id, topic, q}]),
// `pending` ({q, entry, seedId, n}: a question to ask now; a new `n` asks
// again), `scope` ('college_page' | 'ask_link' | 'news', for the events), `onClose`.
// A News sheet (#3272): `story` (its slug, for the events), `school` null for a
// general story, `words` ({line, greeting, placeholder, closeLabel}) in place
// of the school's, and `nudge` ({key, line}) in place of the topic nudge.
// A distress turn's topic never leaves as analytics (#4294): it is counted
// in the server's content-free ledger only, and here reported as 'other'.
const topicOut = (t) => (t === 'distress' ? 'other' : t);

export default function AstraChat({ onStart, onUnavailable, signedIn = false, buildNames = [],
    variant = 'hero', school = null, seeds = null, pending = null, scope = null, onClose = null,
    story = null, words = null, nudge: nudgeOverride = null }) {
    const sheet = variant === 'sheet' && (Boolean(school) || Boolean(story));
    const short = school ? (school.shortName || school.name) : null;
    const sheetWords = {
        line: words?.line || SHEET.line(short),
        greeting: words?.greeting || SHEET.greeting(short),
        placeholder: words?.placeholder || SHEET.placeholder(short),
        closeLabel: words?.closeLabel || SHEET.closeLabel(short),
    };
    // A sheet's own sign-in line (News) keeps its words and leads with the
    // read like any topic when the answer named a school (#5229).
    const nudgeOf = (topic, sch) => (nudgeOverride
        ? { ...nudgeOverride, read: readLeadFor(nudgeOverride.key, sch), school: sch || null }
        : nudgeFor(topic, sch));
    const sheetAnswered = useRef(false);
    // what every event from the sheet adds: where it was opened, the school,
    // and on a News story the story
    const scoped = sheet ? { scope: scope || 'college_page', school_id: school?.id || null, ...(story ? { story } : {}) } : {};
    // Where a new conversation started (#3473): the landing page's chat, or
    // the sheet's scope (a college page, a /ask link from a Short, a News story).
    const origin = sheet ? (scope || 'college_page') : 'home';
    const [token, setToken] = useState(() => chatToken());
    // The conversation so far, kept for this tab's session (#2809): back from
    // a college page (the Back to Astra control, the browser's back button,
    // the logo) or a reload finds it where it was.
    // Restored AFTER mount (review of #2809): the landing page is prerendered
    // and hydrated, so the first render must match the empty chat it was
    // built with; the kept thread arrives in the mount effect below.
    const [restored, setRestored] = useState(false);
    const [thread, setThread] = useState([]);        // {role, text, answer?, kind, ...}
    const [tab, setTab] = useState(CHAT_TABS[0].key);
    const [asking, setAsking] = useState(false);
    const [error, setError] = useState(null);
    const [total, setTotal] = useState(null);        // the live "free questions a day"
    const [left, setLeft] = useState(null);
    const [limit, setLimit] = useState(false);
    const [closed, setClosed] = useState(false);     // under 13: the conversation ends
    const nudgedRef = useRef(false);                 // the nudge shows once a conversation
    const [topics, setTopics] = useState([]);
    const threadRef = useRef(null);
    const [personal, setPersonal] = useState(null);  // null | {school}  (the picture flow is open)
    const personalRef = useRef(null);
    // Where the view goes when the personal step opens or closes (#2886): the
    // form lands under the last answer, below the fold on a long
    // conversation, so the page brings it up. 'personal' | 'back' | null.
    const moveRef = useRef(null);
    const [hp, setHp] = useState('');
    const [restore, setRestore] = useState(null);      // a question that did not go through, back in the box
    // The answer as it is written (#2809): on when the server says so.
    const [streamOn, setStreamOn] = useState(false);
    const [streamText, setStreamText] = useState('');
    const shownRef = useRef(false);
    // The "Most asked this week" block (#5258), as the server ranked it; null
    // while the flag is off for this caller (the chat opens exactly as before).
    const [popular, setPopular] = useState(null);

    useEffect(() => {
        if (!shownRef.current) {
            shownRef.current = true;
            track(EVENTS.ASTRA_CHAT_SHOWN, { variant: sheet ? 'sheet' : 'B', ...scoped });
        }
        let alive = true;
        astraStatus().then((s) => {
            if (!alive) return;
            // `chat` must be said, not assumed: a route that predates the
            // open chat (a deploy in flight) answers status without it.
            if (!s.success || s.chat !== true) { onUnavailable?.(); return; }
            if (Number.isInteger(s.free_questions) && s.free_questions > 0) setTotal(s.free_questions);
            setStreamOn(s.stream === true);
            if (s.popular && typeof s.popular === 'object') setPopular(s.popular);
        });
        return () => { alive = false; };
    }, []);   // eslint-disable-line react-hooks/exhaustive-deps

    // The kept conversation, once, after mount. The daily limit and the
    // questions left are NOT kept (review of #2809: a tab reopened the next
    // day stayed locked); the server says both again on the next question.
    useEffect(() => {
        // The sheet starts clean: a landing conversation about another school
        // never shows under "Asking about <School>".
        const kept = sheet ? null : loadChatThread();
        if (kept) {
            setThread(kept.thread);
            setTopics(kept.topics);
            if (kept.total) setTotal((t) => t ?? kept.total);
            nudgedRef.current = kept.nudged;
        }
        setRestored(kept ? 'kept' : true);
    }, []);
    // Keep the thread as it changes (never before the restore has run, or the
    // empty first render would erase it). An under-13 conversation is never
    // kept (nothing about a child outlives the view, #2600).
    useEffect(() => {
        if (!restored) return;
        if (closed) { clearChatThread(); return; }
        // The sheet starts empty: saving that would erase the landing
        // conversation kept for this tab before the visitor asks anything
        // (review of #3262; "Back to Astra" would find it gone).
        if (sheet && !thread.length) return;
        saveChatThread({ thread, topics, total, nudged: nudgedRef.current });
    }, [restored, thread, topics, total, closed]);
    // Back on a kept conversation: its last answer in view, once.
    useEffect(() => {
        if (restored !== 'kept') return undefined;
        // after the route's own scroll to the top has run
        const t = setTimeout(() => {
            bringIntoView(lastAnswerIn(threadRef.current), sheet);
        }, 150);
        return () => clearTimeout(t);
    }, [restored]);

    // "Make it about me" opened: the step's top just under the header and the
    // caret in the form's box. "Not now": the last answer in view again, the
    // question box back under it.
    useEffect(() => {
        const move = moveRef.current;
        moveRef.current = null;
        if (move === 'personal' && personalRef.current) {
            bringIntoView(personalRef.current, sheet, ANSWER_PEEK);
            const box = personalRef.current.querySelector('textarea');
            try { box?.focus({ preventScroll: true }); } catch { box?.focus(); }
        } else if (move === 'back') {
            bringIntoView(lastAnswerIn(threadRef.current), sheet);
        }
    }, [personal]);   // eslint-disable-line react-hooks/exhaustive-deps

    // In the sheet the newest turn comes into view: the question as it is
    // sent, then the START of its answer (a card may follow it).
    useEffect(() => {
        if (!sheet || !thread.length) return;
        const el = threadRef.current;
        const last = thread[thread.length - 1];
        const asked = el?.querySelectorAll('[data-turn="visitor"]');
        bringIntoView(last.role === 'astra' ? lastAnswerIn(el) : asked && asked[asked.length - 1], true);
    }, [sheet, thread.length]);   // eslint-disable-line react-hooks/exhaustive-deps

    // Start over: a clean thread in this tab. The day's count and the
    // conversation token stay (the count is the day's, not the thread's).
    const startOver = () => {
        clearChatThread();
        setThread([]);
        setTopics([]);
        setError(null);
        setRestore(null);
        nudgedRef.current = false;
        track(EVENTS.ASTRA_CHAT_START_OVER, {});
    };

    const keep = (t) => { setToken(t); keepChatToken(t); };
    const depth = thread.filter((m) => m.role === 'visitor').length;

    const ask = async (message, { entry = 'typed', tab: fromTab = null } = {}, retried = false) => {
        let q = String(message || '').trim().slice(0, 500);
        if (sheet && school && q && !sheetAnswered.current) q = scopedQuestion(q, school).slice(0, 500);
        if (!q || asking || limit || closed) return;
        setAsking(true);
        setError(null);
        const history = thread.slice(-MAX_HISTORY).map((m) => ({ role: m.role, text: m.text }));
        if (!retried) setThread((t) => [...t, { role: 'visitor', text: q }]);
        const askOpts = { entry, tab: fromTab, hp, tMs: await pageTimeForAsk(), origin };
        setStreamText('');
        let data = streamOn
            ? await astraChatAskStream(retried ? null : token, q, history, askOpts, setStreamText)
            : null;
        if (!data) data = await astraChatAsk(retried ? null : token, q, history, askOpts);
        setStreamText('');
        setAsking(false);
        if (data.status === 401 && data.expired && !retried) {
            keep(null);                      // a stale tab: start a fresh chat with the same question
            setAsking(false);
            await ask(q, { entry, tab: fromTab }, true);
            return;
        }
        if (Number.isInteger(data.free_questions) && data.free_questions > 0) setTotal(data.free_questions);
        if (data.token) keep(data.token);
        if (data.limit && !data.reply) {
            setLimit(true);
            setLeft(0);
            setThread((t) => t.slice(0, -1));
            track(EVENTS.ASTRA_CHAT_LIMIT, { state: 'shown', topics_n: topics.length });
            return;
        }
        if (data.success && data.under_13) {
            keep(null);
            setClosed(true);
            setThread((t) => [...t, { role: 'astra', text: data.reply, kind: 'answer', answer: { text: data.reply } }]);
            return;
        }
        if (data.success && data.reply) {
            sheetAnswered.current = true;
            const kind = data.crisis ? 'crisis' : 'answer';
            const answer = { text: data.reply, tables: data.tables || [], sources: data.sources || [],
                pages: data.pages || [] };
            // The personal-read card (Set 2 A) when the server offers it; it
            // takes the nudge's place on that answer.
            const offer = kind === 'answer' && data.personalize && data.personalize.name ? data.personalize : null;
            // The nudge: once a conversation, after the first useful answer
            // (one that stood on a read), never after a crisis reply.
            // A nudge the server names (#3600: a question for past applicants'
            // reported results, which open only for a school on the list)
            // shows on that answer every time: it IS the answer's way forward.
            const serverNudge = kind === 'answer' && nudgeByKey(data.nudge, data.school) ? data.nudge : null;
            const nudge = Boolean(serverNudge)
                || (!nudgedRef.current && Boolean(data.useful) && kind === 'answer' && !offer);
            if (nudge) {
                nudgedRef.current = true;
                const shape = (serverNudge && nudgeByKey(serverNudge, data.school)) || nudgeOf(data.topic, data.school);
                track(EVENTS.ASTRA_CHAT_NUDGE, { topic: shape.key, state: 'shown', read: Boolean(shape.read) });
            }
            setThread((t) => [...t, { role: 'astra', text: data.reply, kind, answer, topic: data.topic,
                school: data.school, nudge, nudgeKey: serverNudge, bands: Array.isArray(data.bands) ? data.bands : null,
                personalize: offer }]);
            if (typeof data.questions_left === 'number') setLeft(data.questions_left);
            if (data.topic && data.counted) setTopics((ts) => [data.topic, ...ts.filter((x) => x !== data.topic)]);
            if (offer) {
                track(EVENTS.ASTRA_CHAT_PERSONALIZE, { state: 'offered', entry: 'card', topic: topicOut(data.topic) || 'other',
                    school_id: offer.school_id || null, ...scoped });
            }
            // `preview`: a preview-token browser (the founder's link, our evals) is
            // eval traffic on the server, so the Desk's PostHog-vs-server count
            // leaves it out on both sides (#3012). No crisis flag, ever (#4294):
            // a 988 reply is counted only in the server's content-free ledger.
            track(EVENTS.ASTRA_CHAT_QUESTION_ASKED, {
                source: entry, topic: topicOut(data.topic) || null, depth: depth + 1, counted: !!data.counted,
                left: typeof data.questions_left === 'number' ? data.questions_left : null,
                preview: Boolean(previewToken()), ...scoped,
            });
            if (data.limit) {
                setLimit(true);
                track(EVENTS.ASTRA_CHAT_LIMIT, { state: 'shown', topics_n: topics.length + 1 });
            }
            return;
        }
        // Our failure: the question goes back in the box, never charged.
        // Counted, never worded (#3056): status 0 never reached the server
        // (a dropped connection or a stream cut short), so only this event
        // sees it; anything else the server logged with its reason.
        track(EVENTS.ASTRA_CHAT_FAILED, {
            reason: data.busy ? 'busy' : data.status === 0 ? 'network' : 'server',
            status: Number.isInteger(data.status) ? data.status : null,
        });
        setThread((t) => t.slice(0, -1));
        setRestore({ text: q, n: Date.now() });
        if ((data.busy || data.status === 404 || data.expired) && thread.length === 0 && !signedIn) {
            onUnavailable?.();
            return;
        }
        setError(data.busy ? CHAT.busy : (data.error && data.status !== 404 ? data.error : CHAT.error));
    };

    const pickTab = (key) => {
        setTab(key);
        track(EVENTS.ASTRA_CHAT_TOPIC_TAB, { topic: key });
    };
    // The ARIA tabs pattern: arrows move between tabs, one tab in the Tab order.
    const tabKeys = (e) => {
        const i = CHAT_TABS.findIndex((t) => t.key === tab);
        const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
        if (!step) return;
        e.preventDefault();
        const next = CHAT_TABS[(i + step + CHAT_TABS.length) % CHAT_TABS.length];
        pickTab(next.key);
        document.getElementById(`astra-chat-tab-${next.key}`)?.focus();
    };
    const tapSeed = (seed, position) => {
        track(EVENTS.ASTRA_CHAT_SEED_TAPPED, { seed_id: seed.id, topic: seed.topic, position, ...scoped });
        ask(seed.q, { entry: 'seed', tab: sheet ? null : tab });
    };
    const tapPopular = (chip) => {
        track(EVENTS.ASTRA_POPULAR_TAPPED, { surface: 'lite', topic: chip.topic, rank: chip.rank });
        ask(chip.seed.q, { entry: 'popular' });
    };
    // A question the sheet was opened with (a seed or the box's own typing):
    // asked once per `pending.n`.
    const askedRef = useRef(null);
    useEffect(() => {
        // a question still in flight: this one waits for it (ask() would drop it)
        if (!sheet || !restored || asking || !pending || !pending.q || askedRef.current === pending.n) return;
        askedRef.current = pending.n;
        ask(pending.q, { entry: pending.entry === 'seed' ? 'seed' : 'typed' });
    }, [sheet, restored, asking, pending?.n]);   // eslint-disable-line react-hooks/exhaustive-deps
    // `forSchool`: the card's school ({id, name, shortName, testBlind}); null
    // from the header's "Make it about me". `byName` (#5229): the nudge's
    // school, known here only by the name the answer spoke; the free read
    // resolves it against its own roster and reads it first.
    const openPersonal = (forSchool = null, entry = 'header', byName = null) => {
        moveRef.current = 'personal';
        setPersonal({ school: forSchool, firstSchoolName: byName || null });
        track(EVENTS.ASTRA_CHAT_PERSONALIZE, { state: 'started', entry, topic: topicOut(topics[0]) || 'other',
            school_id: forSchool?.id || null, ...scoped });
    };
    const startFree = (cta) => {
        if (cta === CHAT_CTA.limit) track(EVENTS.ASTRA_CHAT_LIMIT, { state: 'start_free', topics_n: topics.length });
        onStart?.(cta);
    };
    const nudgeClicked = (nudge) => {
        track(EVENTS.ASTRA_CHAT_NUDGE, { topic: nudge.key, state: 'clicked', read: Boolean(nudge.read), cta: 'signin' });
        onStart?.(CHAT_CTA.nudge(nudge.key));
    };
    // The nudge's read button (#5229): the free read, preset to the school.
    const nudgeRead = (nudge) => {
        track(EVENTS.ASTRA_CHAT_NUDGE, { topic: nudge.key, state: 'clicked', read: true, cta: 'read' });
        openPersonal(null, 'nudge', nudge.school);
    };
    const closePersonal = () => {
        moveRef.current = 'back';
        setPersonal(null);
    };
    // Founder, 2026-10-23: a visitor who would rather sign in than describe
    // themselves can, from the picture step, beside Build my picture (#5237).
    const signinInstead = signedIn || !onStart ? null : {
        label: CHAT.signinInstead,
        cta: CHAT_CTA.signinInstead,
        testid: 'astra-chat-signin-instead',
        onClick: () => onStart(CHAT_CTA.signinInstead),
    };
    const notNow = () => {
        track(EVENTS.ASTRA_CHAT_PERSONALIZE, { state: 'not_now', topic: topicOut(topics[0]) || 'other', ...scoped });
        closePersonal();
    };
    const sourceClicked = (kind, topic) => track(EVENTS.ASTRA_CHAT_SOURCE_CLICKED, { kind: kind || 'official', topic: topicOut(topic) || null });

    const current = useMemo(() => CHAT_TABS.find((t) => t.key === tab) || CHAT_TABS[0], [tab]);
    const lastAstra = [...thread].reverse().find((m) => m.role === 'astra');
    const showCounter = total != null && left != null && left <= COUNTER_FROM && !limit && !closed;
    // The week's order (#5258): the landing row's chips and the line under
    // them; on a college page's sheet, its own seeds re-ordered (no label).
    const popularChips = useMemo(() => (sheet ? [] : liteChips(popular)), [sheet, popular]);
    const popularLine = useMemo(() => (sheet ? null : insightLine(popular)), [sheet, popular]);
    const sheetSeeds = useMemo(() => (sheet ? rankSeeds(seeds || [], popular) : seeds), [sheet, seeds, popular]);
    // A tab's seeds leave out the one the row already shows (the mock's
    // Schools tab), unless that would leave the tab empty.
    const tabSeeds = useMemo(() => {
        const shown = new Set(popularChips.map((c) => c.seed.id));
        const rest = current.seeds.filter((s) => !shown.has(s.id));
        return rest.length ? rest : current.seeds;
    }, [current, popularChips]);
    const startScreen = thread.length === 0 && !personal && !limit && !closed;
    const popularShownRef = useRef(false);
    useEffect(() => {
        if (popularShownRef.current || !startScreen) return;
        const ranked = sheet ? Boolean(popular && popular.basis === 'week' && (popular.topics || []).length) : popularChips.length > 0;
        if (!ranked) return;
        popularShownRef.current = true;
        track(EVENTS.ASTRA_POPULAR_SHOWN, { surface: sheet ? 'sheet' : 'lite', basis: popular.basis,
            n: sheet ? (popular.topics || []).length : popularChips.length, ...scoped });
        if (popularLine) track(EVENTS.ASTRA_INSIGHT_SHOWN, { surface: 'lite', kind: 'topic' });
    }, [startScreen, popular, popularChips, popularLine]);   // eslint-disable-line react-hooks/exhaustive-deps
    const sheetSamples = useMemo(() => (sheetSeeds || []).map((s) => ({ key: s.id, text: s.q })), [sheetSeeds]);
    const composer = (
        <AstraComposer question onRun={(text) => ask(text, { entry: 'typed' })} busy={asking}
            samples={sheet && sheetSamples.length ? sheetSamples : QUESTION_SAMPLES} goLabel={CHAT.go} minChars={2}
            ariaLabel={sheet ? sheetWords.placeholder : CHAT.ariaLabel}
            savedLine={CHAT.saved} restore={restore} />
    );

    const notice = (
        <p data-testid="astra-chat-notice"
            className={`border-t border-[#F0EEE8] text-[11px] leading-snug text-[#6B6B6B] ${sheet ? '-mx-4 px-4 pt-3 sm:-mx-5 sm:px-5' : 'px-4 py-3 sm:px-5'}`}>
            {CHAT.notice}
        </p>
    );

    return (
        <div data-testid="astra-chat" data-variant={sheet ? 'sheet' : 'hero'}
            className={sheet ? 'relative flex min-h-0 flex-1 flex-col bg-white text-left'
                : 'relative mt-6 rounded-2xl border border-[#E0DED8] bg-white text-left shadow-sm sm:mt-8'}>
            <Honeypot value={hp} onChange={setHp} />
            <header className={`flex shrink-0 items-center gap-2 border-b border-[#E0DED8] bg-[#1A4D2E] px-4 py-3.5 ${sheet ? '' : 'rounded-t-2xl'}`}>
                <AstraAvatar size={24} />
                <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-white">{sheet ? SHEET.name : CHAT.panelName}</p>
                    <p className="truncate text-[11px] text-[#C7E3CB]">{sheet ? sheetWords.line : CHAT.panelLine}</p>
                </div>
                {sheet && (
                    <button type="button" onClick={() => onClose?.()} data-testid="astra-sheet-close"
                        aria-label={sheetWords.closeLabel}
                        className="min-h-[44px] shrink-0 rounded-full border border-white/40 px-4 text-xs font-semibold text-white hover:bg-white/10">
                        {SHEET.close}
                    </button>
                )}
                {!sheet && !personal && thread.length > 0 && !asking && !closed && (
                    <button type="button" onClick={startOver} data-testid="astra-chat-start-over"
                        aria-label={CHAT.startOverLabel}
                        className="min-h-[44px] shrink-0 rounded-full px-3 text-xs font-semibold text-white underline underline-offset-4 hover:bg-white/10">
                        {CHAT.startOver}
                    </button>
                )}
                {!sheet && !personal && !closed && (
                    <button type="button" onClick={() => openPersonal(null)} data-testid="astra-chat-personalize"
                        className="min-h-[44px] shrink-0 rounded-full border border-white/40 px-3 text-xs font-semibold text-white hover:bg-white/10">
                        {CHAT.personalize}
                    </button>
                )}
            </header>

            {/* One parent, the composer in ONE place (review of #2725: it
                moved between parents on the first answer, which remounted
                it and dropped keyboard focus): the thread above it, the
                tabs and seeds under it until the first question. */}
            <div className={`space-y-3 px-4 py-4 sm:px-5 ${sheet ? 'min-h-0 flex-1 overflow-y-auto overscroll-contain' : ''}`}
                data-testid={sheet ? 'astra-sheet-body' : undefined}>
                {sheet && thread.length === 0 && !asking && !personal && (
                    <div className="space-y-2" data-testid="astra-sheet-start">
                        <AstraBubble testid="astra-sheet-greeting">{sheetWords.greeting}</AstraBubble>
                        {(sheetSeeds || []).map((s, i) => (
                            <button key={s.id} type="button" onClick={() => tapSeed(s, i)} disabled={asking}
                                data-testid="astra-sheet-seed"
                                className="flex min-h-[48px] w-full items-center justify-between gap-3 rounded-xl border border-[#CFE0D2] bg-white px-3.5 py-2.5 text-left text-sm font-medium text-[#1A4D2E] hover:bg-[#F0F7F1] disabled:opacity-60">
                                <span>{s.q}</span>
                                <ArrowRightIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                            </button>
                        ))}
                    </div>
                )}
                {thread.length > 0 && (
                    <div className="space-y-3" aria-live="polite" aria-busy={asking} data-testid="astra-chat-thread" ref={threadRef}>
                        {thread.map((m, i) => (m.role === 'visitor'
                            // eslint-disable-next-line react/no-array-index-key
                            ? <VisitorBubble key={i}>{m.text}</VisitorBubble>
                            : (
                                // eslint-disable-next-line react/no-array-index-key
                                <React.Fragment key={i}>
                                    <RichAnswer answer={m.answer || { text: m.text }} testid={`astra-chat-${m.kind}`}
                                        onSource={(kind) => sourceClicked(kind, m.topic)} />
                                    {m === lastAstra && m.bands && !limit && !closed && (
                                        <div className="pl-9" data-testid="astra-chat-bands">
                                            <p className="mb-1.5 text-xs text-[#4A6B53]">{CHAT.bandsAsk}</p>
                                            <div className="flex flex-wrap gap-2">
                                                {m.bands.map((b) => (
                                                    <button key={b} type="button" disabled={asking}
                                                        onClick={() => ask(b, { entry: 'band' })}
                                                        className="min-h-[44px] rounded-full border border-[#CFE0D2] bg-white px-3 py-2 text-sm font-medium text-[#1A4D2E] hover:bg-[#F0F7F1] disabled:opacity-60">
                                                        {b}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {m === lastAstra && m.personalize && !personal && !closed && !limit && (
                                        <PersonalOffer offer={m.personalize} topic={m.topic}
                                            onOpen={() => openPersonal({ id: m.personalize.school_id, name: m.personalize.name,
                                                shortName: m.personalize.name, testBlind: m.personalize.test_blind === true },
                                            'card')} />
                                    )}
                                    {m.nudge && !closed && (
                                        <Nudge nudge={(m.nudgeKey && nudgeByKey(m.nudgeKey, m.school)) || nudgeOf(m.topic, m.school)}
                                            onStart={nudgeClicked} onRead={nudgeRead} />
                                    )}
                                </React.Fragment>
                            )))}
                        {asking && (streamText
                            ? <RichAnswer answer={{ text: streamText }} testid="astra-chat-streaming" />
                            : <AstraBubble testid="astra-chat-thinking">{CHAT.thinking}</AstraBubble>)}
                    </div>
                )}
                {thread.length === 0 && asking && (streamText
                    ? <RichAnswer answer={{ text: streamText }} testid="astra-chat-streaming" />
                    : <AstraBubble testid="astra-chat-thinking">{CHAT.thinking}</AstraBubble>)}
                {personal && (
                    // "Make it about me" is the conversation's next turn (#2896,
                    // the founder's pick A of the #2886 mocks, 2026-10-02): the
                    // click is the visitor's message, Astra's prompt and the
                    // picture form follow under the last answer, and every
                    // earlier question stays above. The step lives outside
                    // `thread`, so it is never sent as history and never kept
                    // for the session: a reload restores the conversation.
                    <div className={thread.length > 0 ? 'space-y-3 border-t border-[#F0EEE8] pt-4' : 'space-y-3'}
                        data-testid="astra-chat-personal" ref={personalRef}>
                        <VisitorBubble>{CHAT.personalize}</VisitorBubble>
                        <AstraLite embedded chatToken={token} school={personal.school} signedIn={signedIn}
                            firstSchoolName={personal.firstSchoolName || null}
                            composerSecondary={signinInstead}
                            onStart={onStart} onUnavailable={closePersonal}
                            onClosed={() => { keep(null); setClosed(true); }} />
                        {!closed && (
                            // Live 2026-10-02 at 375px: pressing this blurred the
                            // form's empty box, its "Try this" line came back,
                            // the button dropped 44px under the pointer and the
                            // click never landed. Keeping focus until the click
                            // holds the layout still; the keyboard is unaffected.
                            <button type="button" onClick={notNow} onMouseDown={(e) => e.preventDefault()}
                                data-testid="astra-chat-not-now"
                                className="ml-9 min-h-[44px] text-sm font-semibold text-[#1A4D2E] underline underline-offset-4">
                                {CHAT.notNow}
                            </button>
                        )}
                    </div>
                )}
                {error && !personal && <p role="alert" className="pl-9 text-sm text-[#C05838]">{error}</p>}
                {personal ? null : limit ? (
                    <LimitCard total={total} topics={topics} buildNames={buildNames} onStart={startFree} />
                ) : !closed && (
                    <div className={thread.length > 0 ? 'space-y-3 border-t border-[#F0EEE8] pt-4' : 'space-y-3'}>
                        {showCounter && (
                            <p data-testid="astra-chat-counter" aria-live="polite"
                                className={`text-xs font-semibold ${left <= 1 ? 'text-[#C05838]' : 'text-[#4A6B53]'}`}>
                                {CHAT.counter(left, total)}
                            </p>
                        )}
                        {composer}
                    </div>
                )}
                {!sheet && startScreen && (
                    <>
                        {popularChips.length > 0 && (
                            <div role="group" aria-label={POPULAR.liteLabel} data-testid="astra-popular">
                                <p aria-hidden="true" className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.07em] text-[#6B6B6B]">
                                    {POPULAR.liteLabel}
                                </p>
                                <div className="space-y-1.5">
                                    {popularChips.map((c) => (
                                        <button key={c.seed.id} type="button" onClick={() => tapPopular(c)} disabled={asking}
                                            data-testid="astra-popular-chip" data-topic={c.topic}
                                            className="flex min-h-[44px] w-full items-center rounded-xl border border-[#CFE0D2] bg-[#F4F8F3] px-3.5 py-2 text-left text-[13px] font-semibold leading-snug text-[#1A4D2E] hover:bg-[#EAF3EB] disabled:opacity-60">
                                            {c.seed.q}
                                        </button>
                                    ))}
                                </div>
                                {popularLine && (
                                    <p data-testid="astra-popular-insight" className="mt-2 text-[12.5px] leading-snug text-[#3C463E]">
                                        {popularLine}
                                    </p>
                                )}
                            </div>
                        )}
                        <div role="tablist" aria-label={CHAT.tabsLabel} onKeyDown={tabKeys}
                            className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 [scrollbar-width:none] sm:mx-0 sm:px-0 [&::-webkit-scrollbar]:hidden">
                            {CHAT_TABS.map((t) => (
                                <button key={t.key} type="button" role="tab" aria-selected={t.key === tab}
                                    tabIndex={t.key === tab ? 0 : -1}
                                    aria-controls="astra-chat-seeds" id={`astra-chat-tab-${t.key}`}
                                    onClick={() => pickTab(t.key)} data-testid={`astra-chat-tab-${t.key}`}
                                    className={`min-h-[44px] shrink-0 rounded-full px-4 text-sm font-semibold ${t.key === tab
                                        ? 'bg-[#1A4D2E] text-white' : 'border border-[#CFE0D2] bg-white text-[#1A4D2E]'}`}>
                                    {t.label}
                                </button>
                            ))}
                        </div>
                        <div className="space-y-2" role="tabpanel" id="astra-chat-seeds"
                            aria-labelledby={`astra-chat-tab-${current.key}`} data-testid="astra-chat-seeds">
                            {tabSeeds.map((s, i) => (
                                <button key={s.id} type="button" onClick={() => tapSeed(s, i)} disabled={asking}
                                    data-testid="astra-chat-seed"
                                    className="flex min-h-[48px] w-full items-center justify-between gap-3 rounded-xl border border-[#CFE0D2] bg-white px-3.5 py-2.5 text-left text-sm font-medium text-[#1A4D2E] hover:bg-[#F0F7F1] disabled:opacity-60">
                                    <span>{s.q}</span>
                                    <ArrowRightIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                                </button>
                            ))}
                        </div>
                    </>
                )}
                {sheet && notice}
            </div>
            {!sheet && notice}
        </div>
    );
}

// The hero block: Astra's mark, the founder's headline, the line under it,
// and the chat. The page wraps it in the #see-your-odds section.
// `heading` and `sub`: the landing page's story order (#5128) puts its promise
// here; without them, the chat's own words. The chat box is the same either way.
export function AstraChatHero({ heading = null, sub = null, ...props }) {
    return (
        <div data-testid="astra-chat-hero" className="mx-auto max-w-3xl px-4 text-center">
            <div className="flex justify-center"><AstraAvatar size={56} /></div>
            <h1 className="mt-3 font-serif text-[30px] font-bold leading-[1.1] text-[#1A4D2E] [text-wrap:balance] sm:text-5xl">{heading || CHAT.heading}</h1>
            <p className="mx-auto mt-3 max-w-xl text-[15px] leading-relaxed text-[#4A4A4A] [text-wrap:pretty] sm:text-lg">{sub || CHAT.sub(UNIVERSITY_COUNT)}</p>
            <AstraChat {...props} />
        </div>
    );
}
